import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtIDR } from "@/lib_app/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Avatar from "@/components/app/Avatar";
import {
  Building2, Search, Plus, Trash2, Globe,
  LayoutGrid, Rows3, MoreVertical, CheckSquare,
} from "lucide-react";
import Pagination, { paginate } from "@/components/app/Pagination";
import { toast } from "sonner";
import ClientDetailModal from "@/components/app/ClientDetailModal";

const CAT = {
  pemerintah: { label: "Pemerintah", tint: "bg-red-100 text-red-700" },
  bumn:       { label: "BUMN",        tint: "bg-blue-100 text-blue-700" },
  swasta:     { label: "Swasta",      tint: "bg-[#C9F0D6] text-[#4A6D53]" },
  partner:    { label: "Partner",     tint: "bg-[#DCE0E7] text-[#626061]" },
};

const VIEW_KEY = "sp_clients_view_mode";

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [roleTab, setRoleTab] = useState("customer");
  const [salesFilter, setSalesFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Support ?open=<client_id> from Global Search / deep links
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setEditingId(openId);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_KEY) || "table");
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [kebabOpen, setKebabOpen] = useState(false);
  const kebabRef = useRef(null);
  const { user: me } = useAuth();
  const canDelete = me?.role === "owner" || me?.role === "sales_director";

  useEffect(() => { localStorage.setItem(VIEW_KEY, viewMode); }, [viewMode]);

  // Close kebab on outside click
  useEffect(() => {
    if (!kebabOpen) return;
    const h = (e) => { if (kebabRef.current && !kebabRef.current.contains(e.target)) setKebabOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [kebabOpen]);

  const exitSelectionMode = () => { setSelectionMode(false); setSelectedIds(new Set()); };

  const load = async () => {
    try {
      const [c, u] = await Promise.all([api.get("/clients"), api.get("/users")]);
      setRows(c.data);
      setUsers(u.data);
      setSelectedIds(new Set());
      setSelectionMode(false);
    } catch { toast.error("Gagal memuat klien"); }
  };
  useEffect(() => { load(); }, []);

  const toggleOne = (id) => setSelectedIds((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleAll = (visible) => setSelectedIds((s) => {
    const allSelected = visible.every((r) => s.has(r.id));
    if (allSelected) return new Set([...s].filter((id) => !visible.some((r) => r.id === id)));
    const n = new Set(s); visible.forEach((r) => n.add(r.id)); return n;
  });

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Hapus ${ids.length} klien terpilih?\n\nKlien yang masih punya prospek terkait tidak akan dihapus.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/clients/bulk-delete", { client_ids: ids });
      const parts = [];
      if (r.data.deleted_count > 0) parts.push(`${r.data.deleted_count} dihapus`);
      if (r.data.skipped_count > 0) {
        const names = (r.data.skipped || []).slice(0, 3).map((s) => s.name).join(", ");
        const more = r.data.skipped.length > 3 ? ` +${r.data.skipped.length - 3} lainnya` : "";
        parts.push(`${r.data.skipped_count} dilewati (${names}${more} masih punya prospek)`);
      }
      if (r.data.deleted_count > 0) toast.success(parts.join(" · "));
      else toast.warning(parts.join(" · ") || "Tidak ada yang dihapus");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBulkBusy(false); }
  };

  const cycleSort = (key) => {
    if (sortBy !== key) { setSortBy(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortBy(null); setSortDir(null);
  };

  const filtered = useMemo(() => {
    const qs = q.trim().toLowerCase();
    let arr = rows.filter((r) => {
      if (roleTab === "customer" && r.is_customer === false) return false;
      if (roleTab === "vendor" && !r.is_vendor) return false;
      if (catFilter !== "all" && r.category !== catFilter) return false;
      if (salesFilter !== "all" && r.sales_id !== salesFilter) return false;
      if (qs && !r.name.toLowerCase().includes(qs)) return false;
      return true;
    });
    if (sortBy && sortDir) {
      const dir = sortDir === "asc" ? 1 : -1;
      const acc = {
        name: (r) => (r.name || "").toLowerCase(),
        category: (r) => r.category || "",
        pic: (r) => (((r.pics || []).find((p) => p.is_default) || (r.pics || [])[0])?.name || "").toLowerCase(),
        sales: (r) => (r.sales_nickname || r.sales_name || "").toLowerCase(),
        prospect_count: (r) => (roleTab === "vendor" ? (r.po_count || 0) : (r.prospect_count || 0)),
        total_deal_value: (r) => (roleTab === "vendor" ? (r.po_total_value || 0) : (r.total_deal_value || 0)),
      };
      arr = [...arr].sort((a, b) => {
        const va = acc[sortBy](a);
        const vb = acc[sortBy](b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }
    return arr;
  }, [rows, q, catFilter, salesFilter, sortBy, sortDir, roleTab]);

  // ---- Pagination ----
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem("sp_clients_page_size");
    return ["25", "50", "100", "all"].includes(saved) ? saved : "25";
  });
  const [page, setPage] = useState(1);
  useEffect(() => { localStorage.setItem("sp_clients_page_size", pageSize); }, [pageSize]);
  // Reset to page 1 whenever filter or size changes
  useEffect(() => { setPage(1); }, [q, catFilter, salesFilter, sortBy, sortDir, pageSize]);
  const { paged, totalPages, curPage, startIdx, endIdx } = paginate(filtered, pageSize, page);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Database</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Mitra</h1>
          <p className="text-sm text-[#A0A3A8] mt-1">
            {roleTab === "vendor"
              ? "Vendor / supplier beserta rekening pembayarannya — dipakai saat membuat Purchase Order."
              : "Klien terbentuk otomatis dari prospek. Klik baris untuk melengkapi info & upload logo."}
          </p>
          <div className="inline-flex bg-white card-soft rounded-full p-1 gap-1 mt-3" data-testid="partner-role-tabs">
            {[["customer", "Klien", rows.filter((r) => r.is_customer !== false).length], ["vendor", "Vendor", rows.filter((r) => r.is_vendor).length]].map(([k, l, n]) => (
              <button
                key={k} onClick={() => setRoleTab(k)} data-testid={`partner-tab-${k}`}
                className={`h-9 px-4 rounded-full text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${
                  roleTab === k ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F4F5F7]"
                }`}
              >
                {l}
                <span className={`min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${roleTab === k ? "bg-white/25" : "bg-[#F4F5F7]"}`}>{n}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="inline-flex bg-white card-soft rounded-full p-1 gap-1">
            <button
              data-testid="client-view-table"
              onClick={() => setViewMode("table")}
              className={`h-9 px-3 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                viewMode === "table" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
              }`}
              title="Tampilan Tabel"
            >
              <Rows3 className="w-3.5 h-3.5" /> Tabel
            </button>
            <button
              data-testid="client-view-card"
              onClick={() => setViewMode("card")}
              className={`h-9 px-3 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                viewMode === "card" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
              }`}
              title="Tampilan Kartu"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Kartu
            </button>
          </div>
          <Button
            onClick={() => setEditingId("new")}
            data-testid="add-client-btn"
            className="gap-2 rounded-full h-11 bg-[#090808] hover:bg-[#000000] px-5 shadow-[0_6px_16px_rgba(9,8,8,0.25)]"
          >
            <Plus className="w-4 h-4" /> Tambah Mitra
          </Button>
        </div>
      </div>

      {/* Filters row (below header, matches Prospek) */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A3A8]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari klien..."
            data-testid="client-search"
            className="pl-11 h-12 rounded-full bg-white card-soft border-transparent"
          />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="client-cat-filter" className="w-44 h-12 rounded-full bg-white card-soft border-transparent px-5">
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {Object.entries(CAT).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={salesFilter} onValueChange={setSalesFilter}>
          <SelectTrigger data-testid="client-sales-filter" className="w-52 h-12 rounded-full bg-white card-soft border-transparent px-5">
            <SelectValue placeholder="Tim" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tim</SelectItem>
            {users.filter((u) => u.is_active !== false && u.role !== "owner" && u.role !== "finance").map((u) => (
              <SelectItem key={u.user_id} value={u.user_id}>
                <div className="flex items-center gap-2">
                  <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                  <span className="text-sm font-semibold">{u.nickname || u.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canDelete && (
          <div className="relative" ref={kebabRef}>
            <button
              type="button"
              onClick={() => setKebabOpen((v) => !v)}
              data-testid="client-kebab-btn"
              title="Aksi lain"
              aria-label="Aksi lain"
              aria-expanded={kebabOpen}
              className={`w-12 h-12 rounded-full bg-white card-soft flex items-center justify-center transition-colors ${
                selectionMode ? "text-[#090808]" : "text-[#626061] hover:text-[#090808]"
              }`}
            >
              <MoreVertical className="w-4 h-4" strokeWidth={2.2} />
            </button>
            {kebabOpen && (
              <div
                data-testid="client-kebab-menu"
                className="absolute right-0 top-14 z-30 w-56 rounded-2xl bg-white card-soft border border-[#DCE0E7] py-1.5 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => {
                    setKebabOpen(false);
                    if (selectionMode) exitSelectionMode(); else setSelectionMode(true);
                  }}
                  data-testid="client-toggle-selection-mode"
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#090808] hover:bg-[#F5F3FF] transition-colors"
                >
                  <CheckSquare className="w-4 h-4 text-[#090808]" />
                  <span className="font-medium">{selectionMode ? "Keluar mode pilih" : "Aktifkan mode pilih"}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk action bar (only visible in selection mode with items selected) */}
      {selectionMode && selectedIds.size > 0 && canDelete && (
        <div
          className="flex items-center justify-between gap-3 px-5 py-3 rounded-2xl bg-[#090808] text-white shadow-[0_10px_30px_rgba(24,27,51,0.20)]"
          data-testid="client-bulk-bar"
        >
          <div className="flex items-center gap-3 text-sm">
            <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-white text-[#090808] text-[11px] font-bold">
              {selectedIds.size}
            </span>
            <span>klien dipilih</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exitSelectionMode}
              data-testid="client-bulk-clear"
              className="text-xs text-white/70 hover:text-white px-3 h-8 rounded-md"
            >
              Batal
            </button>
            <Button
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="client-bulk-delete-btn"
              className="gap-1.5 h-9 bg-[#FF6B7D] hover:bg-rose-600"
            >
              <Trash2 className="w-4 h-4" /> {bulkBusy ? "Menghapus..." : `Hapus ${selectedIds.size} Klien`}
            </Button>
          </div>
        </div>
      )}

      {/* Info banner when selection mode active but nothing selected yet */}
      {selectionMode && selectedIds.size === 0 && canDelete && (
        <div
          data-testid="client-selection-hint"
          className="flex items-center justify-between gap-3 px-5 py-3 rounded-2xl bg-[#F5F3FF] border border-[#DCE0E7]"
        >
          <div className="text-xs text-[#090808] font-semibold flex items-center gap-2">
            <CheckSquare className="w-4 h-4" />
            Mode pilih aktif — centang klien yang ingin dihapus
          </div>
          <button
            onClick={exitSelectionMode}
            data-testid="client-selection-exit"
            className="text-[11px] text-[#626061] hover:text-[#090808] font-medium px-2 h-7 rounded-md"
          >
            Keluar
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <Card className="p-10 border-0 rounded-2xl card-soft text-center">
          <Building2 className="w-10 h-10 mx-auto text-[#A0A3A8] mb-2" />
          <div className="text-sm font-semibold text-[#090808]">Belum ada client</div>
          <div className="text-xs text-[#A0A3A8] mt-1">Buat prospek pertama, client akan terbentuk otomatis.</div>
        </Card>
      ) : (
        <>
          {viewMode === "table" ? (
            <ClientTable
              rows={paged}
              roleTab={roleTab}
              onEdit={setEditingId}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={cycleSort}
              selectedIds={selectedIds}
              onToggleOne={toggleOne}
              onToggleAll={() => toggleAll(paged)}
              canSelect={canDelete && selectionMode}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="client-grid">
              {paged.map((c) => (
                <ClientCard
                  key={c.id}
                  client={c}
                  roleTab={roleTab}
                  onEdit={() => setEditingId(c.id)}
                  selected={selectedIds.has(c.id)}
                  onToggleSelect={() => toggleOne(c.id)}
                  canSelect={canDelete && selectionMode}
                />
              ))}
            </div>
          )}
          <Pagination
            total={filtered.length}
            page={curPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            onPageChange={setPage}
            startIdx={startIdx}
            endIdx={endIdx}
            label="client"
            testidPrefix="client-pagination"
          />
        </>
      )}

      <ClientDetailModal
        clientId={editingId}
        users={users}
        roleTab={roleTab}
        onClose={() => setEditingId(null)}
        onSaved={load}
      />
    </div>
  );
}

function ClientLogo({ logo, name, size = "md" }) {
  const px = size === "sm" ? "w-8 h-8 text-[10px]" : size === "lg" ? "w-16 h-16 text-lg" : "w-11 h-11 text-xs";
  const initials = (name || "").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  if (logo) {
    return <img src={logo} alt={name} className={`${px} rounded-xl object-cover shrink-0 border border-[#DCE0E7]`} />;
  }
  return (
    <div className={`${px} rounded-xl bg-gradient-to-br from-[#DCE0E7] to-[#DCE0E7] text-[#090808] font-bold flex items-center justify-center shrink-0`}>
      {initials || <Building2 className="w-4 h-4" />}
    </div>
  );
}

function ClientTable({ rows, roleTab, onEdit, sortBy, sortDir, onSort, selectedIds, onToggleOne, onToggleAll, canSelect }) {
  const allSelected = canSelect && rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someSelected = canSelect && rows.some((r) => selectedIds.has(r.id));
  const isVendorTab = roleTab === "vendor";
  return (
    <Card className="border-0 rounded-2xl card-soft overflow-hidden" data-testid="client-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F5F7] text-[10px] uppercase tracking-widest text-[#626061] font-bold">
            <tr>
              {canSelect && (
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    data-testid="client-select-all"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                    onChange={onToggleAll}
                    className="w-4 h-4 rounded border-[#A0A3A8] accent-[#090808] cursor-pointer"
                  />
                </th>
              )}
              <th className="text-left px-5 py-3"><SortTh label="Klien" sortKey="name" sortBy={sortBy} sortDir={sortDir} onClick={onSort} /></th>
              <th className="text-left px-3 py-3"><SortTh label="Kategori" sortKey="category" sortBy={sortBy} sortDir={sortDir} onClick={onSort} /></th>
              <th className="text-left px-3 py-3"><SortTh label="PIC Utama" sortKey="pic" sortBy={sortBy} sortDir={sortDir} onClick={onSort} /></th>
              <th className="text-left px-3 py-3"><SortTh label="Sales" sortKey="sales" sortBy={sortBy} sortDir={sortDir} onClick={onSort} /></th>
              <th className="text-right px-3 py-3"><SortTh label="Prospek" sortKey="prospect_count" sortBy={sortBy} sortDir={sortDir} onClick={onSort} align="right" /></th>
              <th className="text-right px-5 py-3"><SortTh label="Total Deal" sortKey="total_deal_value" sortBy={sortBy} sortDir={sortDir} onClick={onSort} align="right" /></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const cat = CAT[c.category] || CAT.swasta;
              const defaultPic = (c.pics || []).find((p) => p.is_default) || (c.pics || [])[0];
              const total = isVendorTab ? (c.po_count || 0) : (c.prospect_count || 0);
              const line1Count = isVendorTab ? (c.po_without_project_count || 0) : ((c.active_count || 0) + (c.lost_count || 0));
              const line1Label = isVendorTab ? "PO" : "Prospek";
              const line2Count = isVendorTab ? (c.po_with_project_count || 0) : (c.won_count || 0);
              const dealValue = isVendorTab ? (c.po_total_value || 0) : (c.total_deal_value || 0);
              return (
                <tr
                  key={c.id}
                  data-testid={`client-row-${c.id}`}
                  onClick={() => onEdit(c.id)}
                  className={`border-t border-[#DCE0E7] hover:bg-[#F4F5F7] cursor-pointer transition-colors ${
                    canSelect && selectedIds.has(c.id) ? "bg-[#F4F5F7]/60" : ""
                  }`}
                >
                  {canSelect && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`client-select-${c.id}`}
                        checked={selectedIds.has(c.id)}
                        onChange={() => onToggleOne(c.id)}
                        className="w-4 h-4 rounded border-[#A0A3A8] accent-[#090808] cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <ClientLogo logo={c.logo} name={c.name} />
                      <div className="min-w-0">
                        <div className="font-bold text-[#090808] truncate">{c.name}</div>
                        {c.website && (
                          <div className="text-[11px] text-[#A0A3A8] truncate flex items-center gap-1">
                            <Globe className="w-3 h-3" /> {c.website.replace(/^https?:\/\//, "")}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold ${cat.tint}`}>{cat.label}</span>
                  </td>
                  <td className="px-3 py-3 text-[#626061]">
                    {defaultPic ? (
                      <div className="min-w-0">
                        <div className="font-semibold text-[#090808] truncate">{defaultPic.name}</div>
                        {defaultPic.position && <div className="text-[11px] text-[#A0A3A8] truncate">{defaultPic.position}</div>}
                      </div>
                    ) : (
                      <span className="text-[#A0A3A8]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <Avatar name={c.sales_name} picture={c.sales_picture} size="xs" />
                      <span className="text-[#626061] font-medium truncate">{c.sales_nickname}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right" data-testid={`client-prospect-cell-${c.id}`}>
                    <div className="inline-flex items-center gap-2 justify-end">
                      <div className="font-bold text-[#090808] font-num tabular-nums text-base">{total}</div>
                      {total > 0 && (
                        <div className="flex flex-col items-start justify-center gap-[2px]">
                          {line1Count > 0 && (
                            <span className="text-[9px] font-bold uppercase tracking-wide text-[#8C6318] bg-[#FFF3DA] px-1.5 py-[1px] rounded-full font-num tabular-nums whitespace-nowrap leading-tight">
                              {line1Count} {line1Label}
                            </span>
                          )}
                          {line2Count > 0 && (
                            <span className="text-[9px] font-bold uppercase tracking-wide text-[#3f7a52] bg-[#E4F9EC] px-1.5 py-[1px] rounded-full font-num tabular-nums whitespace-nowrap leading-tight">
                              {line2Count} Proyek
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right font-bold text-[#57D28A]">
                    {dealValue > 0 ? fmtIDR(dealValue) : <span className="text-[#A0A3A8]">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ClientCard({ client, roleTab, onEdit, selected, onToggleSelect, canSelect }) {
  const cat = CAT[client.category] || CAT.swasta;
  const defaultPic = (client.pics || []).find((p) => p.is_default) || (client.pics || [])[0];
  const isVendorTab = roleTab === "vendor";
  const total = isVendorTab ? (client.po_count || 0) : (client.prospect_count || 0);
  const line1Count = isVendorTab ? (client.po_without_project_count || 0) : ((client.active_count || 0) + (client.lost_count || 0));
  const line1Label = isVendorTab ? "PO" : "Prospek";
  const line2Count = isVendorTab ? (client.po_with_project_count || 0) : (client.won_count || 0);
  const dealValue = isVendorTab ? (client.po_total_value || 0) : (client.total_deal_value || 0);
  return (
    <Card
      onClick={onEdit}
      data-testid={`client-card-${client.id}`}
      className={`p-5 border-0 rounded-2xl card-soft cursor-pointer hover:shadow-[0_4px_12px_rgba(24,27,51,0.08)] transition-shadow relative ${
        selected ? "ring-2 ring-[#090808] bg-[#F4F5F7]/40" : ""
      }`}
    >
      {canSelect && (
        <label
          onClick={(e) => e.stopPropagation()}
          className="absolute top-3 right-3 inline-flex items-center justify-center cursor-pointer"
        >
          <input
            type="checkbox"
            data-testid={`client-select-card-${client.id}`}
            checked={!!selected}
            onChange={onToggleSelect}
            className="w-4 h-4 rounded border-[#A0A3A8] accent-[#090808]"
          />
        </label>
      )}
      <div className="flex items-start gap-3 pr-6">
        <ClientLogo logo={client.logo} name={client.name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-sm font-bold text-[#090808] truncate">{client.name}</div>
            <span className={`inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold ${cat.tint}`}>{cat.label}</span>
          </div>
          {defaultPic && (
            <div className="text-xs text-[#A0A3A8] truncate mt-0.5">
              {defaultPic.name}
              {defaultPic.position && <span className="text-[#A0A3A8]"> · {defaultPic.position}</span>}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-[#DCE0E7] flex items-center justify-between text-xs gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar name={client.sales_name} picture={client.sales_picture} size="xs" />
          <span className="text-[#626061] font-medium truncate">{client.sales_nickname}</span>
        </div>
        <div className="text-right shrink-0" data-testid={`client-card-prospect-cell-${client.id}`}>
          <div className="text-[#A0A3A8] text-[10px] uppercase tracking-widest font-semibold">Prospek</div>
          <div className="flex items-center justify-end gap-1.5 mt-0.5">
            <span className="font-bold text-[#090808] font-num tabular-nums text-sm">{total}</span>
            {total > 0 && (
              <div className="flex flex-col items-start justify-center gap-[2px]">
                {line1Count > 0 && (
                  <span className="text-[8px] font-bold uppercase tracking-wide text-[#8C6318] bg-[#FFF3DA] px-1.5 py-[1px] rounded-full font-num tabular-nums whitespace-nowrap leading-tight">
                    {line1Count} {line1Label}
                  </span>
                )}
                {line2Count > 0 && (
                  <span className="text-[8px] font-bold uppercase tracking-wide text-[#3f7a52] bg-[#E4F9EC] px-1.5 py-[1px] rounded-full font-num tabular-nums whitespace-nowrap leading-tight">
                    {line2Count} Proyek
                  </span>
                )}
              </div>
            )}
          </div>
          {dealValue > 0 && (
            <div className="text-[#57D28A] text-[11px] font-bold mt-0.5 font-num tabular-nums">{fmtIDR(dealValue)}</div>
          )}
        </div>
      </div>
    </Card>
  );
}

function SortGlyph({ dir }) {
  const upOp = dir === "desc" ? 0.25 : dir === "asc" ? 1 : 0.55;
  const downOp = dir === "asc" ? 0.25 : dir === "desc" ? 1 : 0.55;
  return (
    <svg width="8" height="11" viewBox="0 0 8 11" aria-hidden="true" className="shrink-0">
      <path d="M4 0.5L7.5 4.2H0.5L4 0.5Z" fill="currentColor" opacity={upOp} />
      <path d="M4 10.5L0.5 6.8H7.5L4 10.5Z" fill="currentColor" opacity={downOp} />
    </svg>
  );
}

function SortTh({ label, sortKey, sortBy, sortDir, onClick, align = "left" }) {
  const active = sortBy === sortKey;
  const dir = active ? sortDir : null;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      data-testid={`client-sort-${sortKey}`}
      className={`inline-flex items-center gap-1.5 uppercase tracking-widest hover:text-[#090808] transition-colors ${active ? "text-[#090808]" : ""}`}
      style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}
    >
      {align === "right" && <SortGlyph dir={dir} />}
      <span>{label}</span>
      {align !== "right" && <SortGlyph dir={dir} />}
    </button>
  );
}

